/*
 * Exercise Performance presentation (EP blueprint §6.7, §6.8). Copy and
 * formatting only.
 *
 * The backend is the engine: states, comparability, the latest factual event,
 * findings and every count arrive computed. Nothing here re-derives a state, a
 * capacity or a threshold; helpers turn structured facts into factual
 * sentences. No sentence carries a verdict or advice (canon §14): the banned
 * words below are checked by a copy-lint test.
 */

import { CURRENT_ANALYSIS } from "../api/insightsApi";
import { TIMEFRAME_LABELS, formatShortDate, muscleLabel } from "./insightsPresentation";

export const EP_STATE = Object.freeze({
  PROGRESSING: "PROGRESSING",
  STABLE: "STABLE",
  LOWER_LATELY: "LOWER_LATELY",
  NOT_ENOUGH_DATA: "NOT_ENOUGH_DATA",
});

// Distribution / tile order (canon §8.2 state list).
export const STATE_ORDER = Object.freeze([
  EP_STATE.PROGRESSING,
  EP_STATE.STABLE,
  EP_STATE.LOWER_LATELY,
  EP_STATE.NOT_ENOUGH_DATA,
]);

export const STATE_LABELS = Object.freeze({
  PROGRESSING: "Progressing",
  STABLE: "Stable",
  LOWER_LATELY: "Lower lately",
  NOT_ENOUGH_DATA: "Not enough data",
});

// §6.7: colour carries no judgement; `danger` is never a state tone.
export const STATE_TONES = Object.freeze({
  PROGRESSING: "action",
  STABLE: "neutral",
  LOWER_LATELY: "info",
  NOT_ENOUGH_DATA: "neutral",
});

// Canon §9.2 reason strings, keyed by the API reason codes.
export const REASON_LABELS = Object.freeze({
  WARMUP_SET: "warm-up set",
  DROP_SET: "drop set",
  UNKNOWN_SET_TYPE: "set type not recognised",
  TIMED_SET: "timed set",
  BAND_RESISTANCE: "band resistance not reconstructible",
  INVALID_LOAD_RECORD: "invalid load record",
  MISSING_LOAD: "missing load",
  DIFFERENT_SERIES: "different series",
  RIR_MISSING: "no RIR logged",
  RIR_5_PLUS: "RIR 5+",
  RIR_4: "RIR 4",
  EFFORT_DIFFERS_FROM_PRESCRIPTION: "effort differs from prescription",
  SPECIAL_TEMPO: "special tempo",
  INTENSIFICATION_METHOD: "method used",
  POSITION_DIFFERS: "position differs",
  SUPERSET_CONTEXT_DIFFERS: "superset context differs",
  RETURN_AFTER_GAP: "first session after a long break",
  REPS_ABOVE_20: "more than 20 reps",
});

// "Other exercises" notes (overview `otherExercises[].reason`, §4.18).
export const OTHER_REASON_NOTES = Object.freeze({
  BAND: "Band resistance isn't measured",
  TIMED_ONLY: "Timed holds aren't compared",
  NO_VALID_LOAD: "Logged loads can't be compared",
  // Covers every set type that cannot be compared (drop sets, unrecognised types).
  NO_WORKING_SET: "No working sets logged to compare",
});

// NOT ENOUGH DATA reasons other than the count (§6.8).
const NOT_ENOUGH_DATA_LINES = Object.freeze({
  NONE_IN_ACTIVE_CYCLE: "No comparable session in this cycle yet",
  SPAN_UNDER_14_DAYS: "Comparable sessions span less than 2 weeks",
  RECENT_SERIES_BREAK: "Recent sessions were logged with a different unit or load type",
});

export const HUB_DATA_BUILDING_LINE = "Comparing your sessions. States appear after 3 comparable sessions per exercise.";
export const HUB_HISTORICAL_NOTE = "States use Current Analysis";

// Words no catalogue string may contain (§6.8 copy lint).
export const BANNED_COPY_WORDS = Object.freeze([
  "plateau", "fatigue", "stalled", "deload", "should", "recommend",
  "increase", "decrease", "too low", "too high", "warning", "score",
]);

// Natural numbers: 100 → "100", 102.5 → "102.5", never "100.0".
export function formatNumber(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return String(Math.round(numeric * 10) / 10);
}

export function formatRir(rir) {
  if (rir === null || rir === undefined) return null;
  return Number(rir) >= 5 ? "5+" : formatNumber(rir);
}

const UNIT_LABELS = Object.freeze({ LB: "lb", KG: "kg" });

// Load in the series' own terms (§6.8): physical weight, the value as entered
// on a machine / equipment, an assistance value, nothing for bodyweight.
export function formatLoad(load) {
  if (!load || load.seriesClass === "NONE" || load.value === null || load.value === undefined) return "";
  const value = formatNumber(load.value);
  const unit = UNIT_LABELS[load.unit] || "";
  if (load.seriesClass === "LOCAL") {
    return load.scaleKind === "MACHINE_VALUE" ? `${value} on this machine` : `${value} on this equipment`;
  }
  if (load.seriesClass === "ASSISTANCE") return `Assist ${value} ${unit}`.trim();
  return `${value} ${unit}`.trim();
}

function atLoad(load) {
  const text = formatLoad(load);
  return text ? ` at ${text}` : "";
}

function repsWithRir(efforts) {
  const sameRir = efforts.every((effort) => effort.rir === efforts[0].rir);
  return efforts
    .map((effort) => (sameRir || effort.rir === null || effort.rir === undefined
      ? formatNumber(effort.reps)
      : `${formatNumber(effort.reps)} (RIR ${formatRir(effort.rir)})`))
    .join(" and ");
}

export function formatNotEnoughData(notEnoughData) {
  if (!notEnoughData) return "";
  const { comparableExposures, required, reasons = [] } = notEnoughData;
  if (comparableExposures < required) return `${comparableExposures} of ${required} comparable sessions`;
  // Never "3 of 3": the first non-count reason explains what is missing.
  const reason = reasons.find((code) => NOT_ENOUGH_DATA_LINES[code]);
  return reason ? NOT_ENOUGH_DATA_LINES[reason] : `${comparableExposures} comparable sessions`;
}

// The per-row factual sentence from `latestEvent` (§4.13, §6.8).
export function formatLatestEvent(event) {
  if (!event) return "";
  switch (event.kind) {
    case "NOT_ENOUGH_DATA":
      return formatNotEnoughData(event);
    case "LOAD_CHANGED":
      return `Moved to ${formatLoad(event.load)} after ${event.previousReps.map(formatNumber).join(" / ")} at ${formatLoad(event.previousLoad)}`;
    case "STEP_UP_AT_LOAD":
      return `${repsWithRir(event.atLoad)} reps${atLoad(event.load)} since ${formatShortDate(event.since)} · after ${event.previousReps.map(formatNumber).join(" / ")} at ${formatLoad(event.previousLoad)}`;
    case "GAIN_AT_LOAD": {
      const efforts = [event.from, ...event.recent];
      const sameRir = efforts.every((effort) => effort.rir === event.from.rir);
      const delta = event.recent[event.recent.length - 1].reps - event.from.reps;
      if (sameRir && delta > 0) return `+${formatNumber(delta)} reps${atLoad(event.load)}`;
      const rirs = efforts.map((effort) => formatRir(effort.rir)).join(" → ");
      return `${formatNumber(event.from.reps)} → ${event.recent.map((effort) => formatNumber(effort.reps)).join(" / ")} reps${atLoad(event.load)} · RIR ${rirs}`;
    }
    case "LOWER_AT_LOAD": {
      const [first, last] = event.lastTwo;
      const sameRir = first.rir === last.rir && first.rir === event.best.rir;
      const delta = event.best.reps - last.reps;
      const week = event.cycleWeek ? ` · week ${event.cycleWeek.weekNumber} of ${event.cycleWeek.totalWeeks}` : "";
      if (sameRir && delta > 0) return `Reps down ${formatNumber(delta)}${atLoad(event.load)} over the last 2 sessions${week}`;
      return `${repsWithRir(event.lastTwo)} reps${atLoad(event.load)} in the last 2 sessions · best ${formatNumber(event.best.reps)} (RIR ${formatRir(event.best.rir)})${week}`;
    }
    case "NO_CHANGE_AT_LOAD":
      return `No change${atLoad(event.load)} across ${formatNumber(event.sessions)} sessions`;
    case "LATEST_AT_LOAD": {
      const rir = formatRir(event.rir);
      return `${formatNumber(event.reps)} reps${atLoad(event.load)}${rir ? ` · RIR ${rir}` : ""} last session`;
    }
    default:
      return "";
  }
}

// "Chest · 10 reps at 100 lb · RIR 2 last session"; no muscle when unknown.
export function formatRowDescription(row) {
  const sentence = formatLatestEvent(row.latestEvent);
  return row.primaryNode ? `${muscleLabel(row.primaryNode)} · ${sentence}` : sentence;
}

export function formatStateCount(count) {
  return formatNumber(count || 0);
}

// Hub rows (§6.3 UX-DEFAULT): LOWER LATELY, then PROGRESSING, then STABLE; the
// API already orders each state by latest exposure, so that order is kept.
export function selectHubRows(exercises, limit = 2) {
  const order = [EP_STATE.LOWER_LATELY, EP_STATE.PROGRESSING, EP_STATE.STABLE];
  return order.flatMap((state) => (exercises || []).filter((row) => row.state === state)).slice(0, limit);
}

// Historical one-liner (OC-1): a factual count, no state.
export function formatHistoricalHubLine(exerciseCount, timeframe) {
  const noun = exerciseCount === 1 ? "exercise" : "exercises";
  if (timeframe === "ALL_TIME") return `${formatNumber(exerciseCount)} ${noun} logged in total`;
  if (timeframe === CURRENT_ANALYSIS) return `${formatNumber(exerciseCount)} ${noun} logged`;
  return `${formatNumber(exerciseCount)} ${noun} logged in the last ${TIMEFRAME_LABELS[timeframe].toLowerCase()}`;
}

export function exerciseDetailPath(exerciseId) {
  return `/progress/exercise-performance/${encodeURIComponent(exerciseId)}`;
}

// Overview screen copy (§6.4). Group order: canon state precedence.
export const OVERVIEW_GROUP_ORDER = Object.freeze([
  EP_STATE.LOWER_LATELY,
  EP_STATE.PROGRESSING,
  EP_STATE.STABLE,
  EP_STATE.NOT_ENOUGH_DATA,
]);
export const OTHER_EXERCISES_TITLE = "Other exercises you've logged";
export const HOW_IT_WORKS_TITLE = "How exercise performance is determined";

// Static, factual explanation (canon §8, §9, §12, §15.3). No numbers the canon
// does not state, no advice.
export const HOW_IT_WORKS_SECTIONS = Object.freeze([
  {
    title: "What is compared",
    body: "Each exercise is compared with itself only: the same exercise, logged the same way. Every session is represented by its hardest comparable working set (for assisted exercises, the least assistance), with its reps and the RIR you logged.",
  },
  {
    title: "Comparable sets",
    body: "A set is comparable when it is a working set with a logged RIR of 3 or less, within 1 of the prescribed RIR when there is one, at the standard tempo, without a special method, at a similar place in the workout and in the same straight-set or superset context. Other sets stay in your history and are not compared.",
  },
  {
    title: "The four states",
    body: "Progressing, Stable and Lower lately use Current Analysis: your current cycle and the previous one. They need at least 3 comparable sessions over 2 weeks or more, one of them in this cycle. Until then the exercise shows Not enough data.",
  },
  {
    title: "No overall rating",
    body: "Exercise Performance describes each exercise on its own. It does not combine exercises into one number, and it does not measure muscle growth directly.",
  },
  {
    title: "Estimated 1RM",
    body: "The estimated 1RM is shown only for compound dumbbell, kettlebell and Olympic barbell exercises. It uses hard, low-rep sets only: RIR 2 or less, with reps plus RIR at 6 or less (for example 6 reps at RIR 0, 5 at RIR 1 or 4 at RIR 2). It never decides a state.",
  },
]);

export function formatOverviewContext(context) {
  const active = context?.activeCycle;
  if (!active) return "";
  const name = active.name || "Current cycle";
  return active.currentWeekNumber && active.totalWeeks
    ? `${name} · Week ${active.currentWeekNumber} of ${active.totalWeeks}`
    : name;
}

export function formatOverviewSummary(summary) {
  const count = summary?.exerciseCount ?? 0;
  const withState = summary?.withStateCount ?? 0;
  return `${formatNumber(count)} ${count === 1 ? "exercise" : "exercises"} · ${formatNumber(withState)} with enough sessions to compare`;
}

// Row meta (§6.4 item 4): the comparable-session count, or for NOT ENOUGH
// DATA the counter / reason line.
export function formatRowMeta(row) {
  if (row.state === EP_STATE.NOT_ENOUGH_DATA) return formatNotEnoughData(row.notEnoughData);
  const count = row.comparableExposures ?? 0;
  return `${formatNumber(count)} comparable ${count === 1 ? "session" : "sessions"}`;
}

// Row description: "Muscle · sentence". A NOT ENOUGH DATA event sentence is
// the counter already shown as meta, so its description is the muscle alone.
export function formatOverviewRowDescription(row) {
  if (row.state === EP_STATE.NOT_ENOUGH_DATA) return row.primaryNode ? muscleLabel(row.primaryNode) : null;
  return formatRowDescription(row);
}

export function formatSessionCount(count) {
  return `${formatNumber(count)} ${count === 1 ? "session" : "sessions"}`;
}

// Historical row (OC-1): "41 sessions · last Oct 16".
export function formatHistoricalRow(row) {
  const last = row.latestSessionDate ? ` · last ${formatShortDate(row.latestSessionDate)}` : "";
  return `${formatSessionCount(row.sessionCount)}${last}`;
}

// ---------------------------------------------------------------------------
// Exercise Detail (§6.5 items 1 and 4, §6.6)

export const DETAIL_STATE_TITLE = "Current Analysis";
export const DETAIL_SESSIONS_TITLE = "Sessions";
export const DETAIL_NO_SESSIONS = "No sessions of this exercise in this period.";
export const DETAIL_CARDIO_LINE = "Cardio isn't analyzed in Exercise Performance.";

// Why a detail has no state (`state.unavailableReason`, §5.3). Band is read
// from the catalog category only; nothing is inferred from the sets.
export function formatDetailUnavailable(state, exercise) {
  switch (state?.unavailableReason) {
    case "NO_ACTIVE_CYCLE":
      return "No active cycle. States use Current Analysis.";
    case "NO_HISTORY_IN_WINDOW":
      return "No sessions of this exercise in Current Analysis yet.";
    case "NOT_STATE_ELIGIBLE":
      return String(exercise?.equipmentCategory || "").toLowerCase() === "band"
        ? "Sessions only — band resistance isn't measured."
        : "Sessions only — these sets aren't compared.";
    default:
      return "";
  }
}

// On a historical timeframe the hero says the state is not about that period.
export function formatHistoricalStateNote(timeframe) {
  if (timeframe === CURRENT_ANALYSIS) return null;
  return `This state uses Current Analysis, not the selected ${TIMEFRAME_LABELS[timeframe].toLowerCase()}.`;
}

// The muted line of a session with no Comparable set: the primary reason the
// API gave for its first set ("Not compared · position differs").
// Which set explains a session with no Comparable set, by the classes the API
// computed (nothing is reclassified here): a Limited set first (it failed only
// a Limited condition), then an Excluded working set, then a drop set, and a
// warm-up only when nothing else carries a reason. Within a tier the API's set
// order holds, and the set's own first reason (the server's ordering) is shown.
const NOT_COMPARED_TIERS = Object.freeze([
  (set) => set.class === "LIMITED",
  (set) => set.class === "EXCLUDED" && set.setType !== "WARMUP" && set.setType !== "DROP_SET",
  (set) => set.class === "EXCLUDED" && set.setType === "DROP_SET",
  (set) => set.class === "EXCLUDED" && set.setType === "WARMUP",
]);

export function formatNotComparedReason(session) {
  const sets = session?.sets || [];
  // No classified set (legacy or incomplete data): make no claim.
  if (!sets.some((set) => set.class)) return null;
  if (sets.some((set) => set.class === "COMPARABLE")) return null;
  const reasoned = sets.filter((set) => set.reasons?.length);
  const tier = NOT_COMPARED_TIERS.find((inTier) => reasoned.some(inTier));
  const reason = tier ? reasoned.find(tier).reasons[0] : null;
  return reason && REASON_LABELS[reason] ? `Not compared · ${REASON_LABELS[reason]}` : "Not compared";
}

// ---------------------------------------------------------------------------
// Performance card and e1RM tab (§6.5 items 2–3, §6.7)

// Axis scale words per `series.scaleLabel` key (§4.16 labels). A LOCAL axis
// never implies a normalized physical weight (canon §13.1).
export const SCALE_LABEL_WORDS = Object.freeze({
  ONE_DUMBBELL: "one dumbbell",
  ONE_KETTLEBELL: "one kettlebell",
  PLATE: "plate",
  OLYMPIC_TOTAL: "total, bar included",
  ADDED: "added weight",
  MACHINE_VALUE: "as entered on this machine",
  SMITH_PER_SIDE: "as entered on this equipment, per side",
  PLATES_LOADED: "plates loaded, as entered",
  LOADED_END: "loaded end, as entered",
  AS_ENTERED: "bar as entered",
});

export const PERFORMANCE_TITLE = "Performance";
export const PERFORMANCE_LEGEND = "RIR 0–1 · RIR 2 · RIR 3 · hollow = not comparable to this program";
export const PERFORMANCE_LEGEND_MISSING_RIR = "grey = no RIR logged";
export const PERFORMANCE_BUCKETS_NOTE = "Each marker is the median of one month's comparable sessions.";
export const PERFORMANCE_NO_DATA = "No working sets to plot in this period.";
// Visually hidden trend table (§6.7): the median values the API computed.
export const TREND_TABLE_CAPTION = "Trend";
export const TREND_VALUE_HEADER = "Trend value";
export function formatTrendPart(index, count) {
  return count > 1 ? `Part ${index + 1} of ${count}` : "";
}
export const E1RM_QUALIFIER = "from sets at RIR 2 or less where reps + RIR is 6 or less";
export const E1RM_NO_POINTS = "No estimate from this period's sessions.";

// "lb · total, bar included" (always with the unit).
export function formatAxisLabel(series) {
  if (!series) return "";
  const unit = UNIT_LABELS[series.unit] || "";
  const words = SCALE_LABEL_WORDS[series.scaleLabel] || "";
  return [unit, words].filter(Boolean).join(" · ");
}

export function formatValueWithUnit(value, unit) {
  if (value === null || value === undefined) return "";
  return `${formatNumber(value)} ${UNIT_LABELS[unit] || ""}`.trim();
}

// "2 sessions at 8 reps · not enough for a trend".
export function formatRepStatus(entry) {
  if (!entry || !entry.notEnoughForTrend) return null;
  return `${formatSessionCount(entry.sessionCount)} at ${formatNumber(entry.reps)} reps · not enough for a trend`;
}

export function formatE1rmStatus(e1rm) {
  if (!e1rm?.notEnoughForTrend) return null;
  const count = (e1rm.points || []).length;
  return count ? `${formatSessionCount(count)} · not enough for a trend` : E1RM_NO_POINTS;
}

// "est. 1RM ~225 lb", or the last date when no recent estimate exists.
export function formatE1rmHeadline(e1rm) {
  if (e1rm?.headline) return `est. 1RM ~${formatValueWithUnit(e1rm.headline.value, e1rm.headline.unit)}`;
  if (e1rm?.noRecentEstimate) return `No recent estimate · last ${formatShortDate(e1rm.noRecentEstimate.lastDate)}`;
  return "";
}

const RIR_BAND_LABELS = Object.freeze({ RIR_0_1: "RIR 0–1", RIR_2: "RIR 2", RIR_3: "RIR 3", NONE: "no RIR logged" });

// A point's RIR band and comparability, as the API classed it:
// "RIR 2 · comparable", "RIR 1 · not comparable · position differs".
export function formatPointDetail(point) {
  const parts = [];
  if (point.rirBand) parts.push(RIR_BAND_LABELS[point.rirBand] || "");
  // Missing RIR is already named by its band; it is never comparable.
  if (point.fill === "MISSING_RIR") return [...parts, "not comparable"].join(" · ");
  if (point.fill === "FILLED") parts.push("comparable");
  else if (point.fill) {
    const reason = (point.reasons || []).map((code) => REASON_LABELS[code]).filter(Boolean)[0];
    parts.push(reason ? `not comparable · ${reason}` : "not comparable");
  }
  return parts.filter(Boolean).join(" · ");
}

// A chart point read out in words: "Oct 9 · 185 lb at 10 reps · RIR 2 · comparable".
export function formatPointReadout(point, { reps, unit }) {
  return [
    formatShortDate(point.date),
    `${formatValueWithUnit(point.value, unit)}${reps ? ` at ${formatNumber(reps)} reps` : ""}`,
    formatPointDetail(point),
  ].filter(Boolean).join(" · ");
}

// Every static string of the catalogue, for the copy lint.
export const COPY_CATALOGUE_STRINGS = Object.freeze([
  ...Object.values(STATE_LABELS),
  ...Object.values(REASON_LABELS),
  ...Object.values(OTHER_REASON_NOTES),
  ...Object.values(NOT_ENOUGH_DATA_LINES),
  HUB_DATA_BUILDING_LINE,
  HUB_HISTORICAL_NOTE,
  OTHER_EXERCISES_TITLE,
  HOW_IT_WORKS_TITLE,
  DETAIL_STATE_TITLE,
  DETAIL_SESSIONS_TITLE,
  DETAIL_NO_SESSIONS,
  DETAIL_CARDIO_LINE,
  ...Object.values(SCALE_LABEL_WORDS),
  PERFORMANCE_TITLE,
  PERFORMANCE_LEGEND,
  PERFORMANCE_LEGEND_MISSING_RIR,
  PERFORMANCE_BUCKETS_NOTE,
  PERFORMANCE_NO_DATA,
  TREND_TABLE_CAPTION,
  TREND_VALUE_HEADER,
  E1RM_QUALIFIER,
  E1RM_NO_POINTS,
  ...HOW_IT_WORKS_SECTIONS.flatMap((section) => [section.title, section.body]),
]);
